import React, { useContext, useReducer, useState } from 'react';

import { InputAdornment, Link, Stack, TextField } from '@mui/material';

import { Mail } from 'lucide-react';

import { LoadingButton } from '../../../Loaders';

import { object, string, ValidationError } from 'yup';

import Axios from '../../../../Assets/Axios/Axios';

import { COLORS } from '../../../../Constant/COLORS';

import ToastContext from '../../../../Context/ToastContext';

import { useFormContext } from '../FormContext';

import sendOTP from '../../../../Helpers/sendOTP';

import PhoneNumberField from '../../../PhoneNumberField';

import { TypoPoppins } from '../../../Typo';

import {
  FindProfessionalByEmail,
  FindUserByEmail,
  FindUserByMobileNumber,
} from '../../../../Server/Query';

function ContactInformation() {
  const { showToastMessage } = useContext(ToastContext);
  const { inputs, openLoginModal, closeModal, codeRefId, nextStep, updateInputs } =
    useFormContext();

  const [isBtnDisabled, setIsBtnDisabled] = useState(false);
  const [errors, updateErrors] = useReducer(errorsReducer, {
    mobileNumber: null,
    email: null,
  });

  function handleClickAlreadyHaveAccount() {
    closeModal();
    openLoginModal();
  }

  function handleChange(event) {
    const { name, value } = event.target;
    updateInputs({ [name]: value });
  }

  async function handleSubmit(event) {
    try {
      event.preventDefault();
      updateErrors({ type: ERROR_ACTIONS.CLEAR });
      setIsBtnDisabled(true);

      const { email, mobileNumber } = await schema.validate(inputs, { abortEarly: false });

      const mobileNumberRequest = Axios(FindUserByMobileNumber({ mobileNumber }));
      const emailRequest = Axios(FindUserByEmail({ email }));
      const professionalExistsWithSameEmailRequest = await Axios(FindProfessionalByEmail({ email }));

      const [mobileNumberResponse, emailResponse, professionalExistsWithSameEmailResponse] =
        await Promise.all([
          mobileNumberRequest,
          emailRequest,
          professionalExistsWithSameEmailRequest,
        ]);

      if (
        mobileNumberResponse?.errors ||
        emailResponse?.errors ||
        professionalExistsWithSameEmailResponse?.errors
      )
        throw new Error('DB failed');

      const isMobileUserExists = Boolean(mobileNumberResponse?.data?.EventBooker);
      const isEmailUserExists = Boolean(emailResponse?.data?.EventBooker);
      const isProfessionalExistsWithSameEmail = Boolean(
        professionalExistsWithSameEmailResponse?.data?.Professional
      );

      if (isMobileUserExists || isEmailUserExists) {
        const errors = {};
        if (isMobileUserExists) errors.mobileNumber = 'Mobile Number is already in use.';
        if (isProfessionalExistsWithSameEmail)
          errors.email = 'Please use a different email to signup.';
        if (isEmailUserExists) errors.email = 'Email is already in use.';

        updateErrors({ type: ERROR_ACTIONS.SET, payload: errors });
        return undefined;
      }

      const response = await sendOTP({ mobileNumber });

      codeRefId.mobileNumber = response.data.referenceId;

      updateInputs({ email, mobileNumber });

      nextStep();
    } catch (error) {
      if (error instanceof ValidationError) {
        const errors = error.inner.reduce((preVal, error) => {
          preVal[error.path] = error.message;
          return preVal;
        }, {});

        updateErrors({ type: ERROR_ACTIONS.SET, payload: errors });
        return undefined;
      }
      showToastMessage('Something went wrong');
    } finally {
      setIsBtnDisabled(false);
    }
  }

  return (
    <Stack
      textAlign='center'
      onSubmit={handleSubmit}
      component='form'
      alignItems='center'
      gap={3}
      sx={{ pb: { xs: 2, md: 12 }, px: { xs: 1, md: 6 }, mt: 2 }}>
      <TypoPoppins>Enter the phone number to verification</TypoPoppins>

      <PhoneNumberField
        fullWidth
        name='mobileNumber'
        placeholder='Mobile number'
        value={inputs.mobileNumber}
        onChange={handleChange}
        helperText={errors.mobileNumber}
        error={Boolean(errors.mobileNumber)}
      />

      <TextField
        spellCheck={false}
        name='email'
        fullWidth
        autoComplete='email'
        placeholder='Email'
        value={inputs.email}
        onChange={handleChange}
        helperText={errors.email}
        error={Boolean(errors.email)}
        inputMode='email'
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <Mail size={18} color={COLORS.inkSoft} strokeWidth={1.8} />
            </InputAdornment>
          ),
        }}
      />

      <LoadingButton color='blue' sx={{ width: '60%', mx: 'auto' }} isLoading={isBtnDisabled} type='submit'>
        <TypoPoppins color='white' fontWeight={500}>
          Send OTP
        </TypoPoppins>
      </LoadingButton>

      <TypoPoppins>
        I have a account?{' '}
        <Link>
          <TypoPoppins
            sx={{ textDecoration: 'underline' }}
            fontWeight={600}
            color={COLORS.blue}
            component='span'
            onClick={handleClickAlreadyHaveAccount}>
            login
          </TypoPoppins>
        </Link>
      </TypoPoppins>
    </Stack>
  );
}

const schema = object({
  mobileNumber: string()
    .trim()
    .matches(/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{3,6}$/im, {
      message: 'Invalid Mobile Number',
    })
    .min(9, 'Invalid Mobile Number')
    .max(12, 'Invalid Mobile Number')
    .transform(value => `+94${value.slice(-9)}`),
  email: string().trim().required().email().lowercase().label('Email'),
});

const ERROR_ACTIONS = Object.freeze({
  SET: 'SET',
  CLEAR: 'CLEAR',
});

function errorsReducer(state, { type, payload }) {
  switch (type) {
    case ERROR_ACTIONS.SET:
      return { ...state, ...payload };
    case ERROR_ACTIONS.CLEAR:
      return Object.keys(state).reduce((errors, key) => {
        errors[key] = null;
        return errors;
      }, {});
    //no default
  }
}

export default ContactInformation;
